import React, { useEffect, useState } from "react";
import { api, formatApiError, fmtINR } from "../lib/apiClient";
import { X, FloppyDisk, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SearchableSelect } from "./SearchableSelect";

// Create / edit a Tally ledger. `initial` = { id?, name, group_id, opening_balance, opening_type }.
export function LedgerFormModal({ initial, groups: groupsProp, onClose, onSaved }) {
  const [groups, setGroups] = useState(groupsProp || []);
  const [f, setF] = useState({ name: "", group_id: "", opening_balance: 0, opening_type: "dr", ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (groupsProp?.length) return;
    api.get("/accounting/groups").then(r => setGroups(r.data)).catch(err => { console.error("Groups load failed:", err); toast.error("Could not load ledger groups"); });
  }, [groupsProp]);
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", h, true);
    return () => window.removeEventListener("keydown", h, true);
  }, [onClose]);
  const save = async (e) => {
    e?.preventDefault();
    if (!f.name.trim() || !f.group_id) return toast.error("Enter a ledger name and pick a group");
    setSaving(true);
    try {
      const body = { name: f.name.trim(), group_id: f.group_id, opening_balance: Number(f.opening_balance || 0), opening_type: f.opening_type };
      const { data } = f.id ? await api.put(`/accounting/ledgers/${f.id}`, body) : await api.post("/accounting/ledgers", body);
      toast.success(`Ledger “${data.name}” ${f.id ? "updated" : "created"}`);
      onSaved(data);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={onClose} data-testid="ledger-form-modal">
      <form onSubmit={save} className="bg-white rounded-lg w-full max-w-md shadow-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-display font-semibold">{f.id ? "Edit Ledger" : "New Ledger"}</div>
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="ledger-form-close"><X size={16} /></button>
        </div>
        <div>
          <div className="label-overline mb-1">Ledger Name *</div>
          <input autoFocus className="field" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="e.g. Bhushan Hardware, Freight Charges, HDFC Bank" data-testid="ledger-form-name" />
        </div>
        <div>
          <div className="label-overline mb-1">Under Group *</div>
          <SearchableSelect testId="ledger-form-group" placeholder="Type to search groups…" value={f.group_id} onChange={v => setF({ ...f, group_id: v })}
            options={groups.map(g => ({ value: g.id, label: g.name, sub: g.nature || g.parent_name || "", keywords: [g.nature, g.parent_name].filter(Boolean) }))} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="label-overline mb-1">Opening Balance (₹)</div>
            <input type="number" step="0.01" className="field text-right" value={f.opening_balance} onChange={e => setF({ ...f, opening_balance: e.target.value })} data-testid="ledger-form-opening" />
          </div>
          <div>
            <div className="label-overline mb-1">Dr / Cr</div>
            <select className="field" value={f.opening_type} onChange={e => setF({ ...f, opening_type: e.target.value })} data-testid="ledger-form-type">
              <option value="dr">Dr (asset / receivable / expense)</option>
              <option value="cr">Cr (liability / payable / income)</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-1.5" data-testid="ledger-form-save"><FloppyDisk size={14} /> {saving ? "Saving…" : f.id ? "Update" : "Create Ledger"}</button>
        </div>
      </form>
    </div>
  );
}

// Dr / Cr ledger pickers for a purchase posting, with Alt+C / "+ New Ledger" inline creation.
export function LedgerPostingPicker({ drId, crId, onChange, vendorName, purchaseType, testPrefix = "post", mode = "purchase", partyName }) {
  const sales = mode === "sales";
  const [ledgers, setLedgers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [modal, setModal] = useState(null); // { side: "dr"|"cr", initial }
  const reload = () => api.get("/accounting/ledgers").then(r => setLedgers(r.data)).catch(err => console.error("Ledgers load failed:", err));
  useEffect(() => { reload(); api.get("/accounting/groups").then(r => setGroups(r.data)).catch(err => console.error("Groups load failed:", err)); }, []);
  const opts = ledgers.map(l => ({ value: l.id, label: l.name, sub: l.group_name, keywords: [l.group_name] }));
  const groupId = (name) => (groups.find(g => g.name === name) || {}).id || "";
  const party = partyName || vendorName || "";
  const partySide = sales ? "dr" : "cr";
  const defaultGroup = (side) => side === partySide ? (sales ? "Sundry Debtors" : "Sundry Creditors")
    : (sales ? "Sales Accounts" : (purchaseType === "inter_state" ? "Purchase Accounts (Inter-State)" : "Purchase Accounts (Intra-State)"));
  const openNew = (side, name) => setModal({ side, initial: { name: name || (side === partySide ? party : ""), group_id: groupId(defaultGroup(side)) } });
  const autoDr = sales ? `${party || "customer"} (Sundry Debtors)` : (purchaseType === "inter_state" ? "Purchase A/c (Inter-State)" : "Purchase A/c (Intra-State)");
  const autoCr = sales ? "Sales A/c" : `${party || "vendor"} (Sundry Creditors)`;
  const drHint = sales ? "customer" : "expense / purchase", crHint = sales ? "sales / income" : "vendor";
  return (
    <div className="border border-dashed border-border rounded-md p-3 space-y-3 md:col-span-2" data-testid={`${testPrefix}-ledger-posting`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="label-overline">Accounting Posting (Tally ledgers)</div>
        <button type="button" onClick={() => openNew("dr", "")} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid={`${testPrefix}-new-ledger`}><Plus size={12} /> New Ledger</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <div className="text-xs text-ink-muted mb-1">Ledger to <b>DEBIT</b> ({drHint}) · default <span className="font-mono">{autoDr}</span></div>
          <SearchableSelect testId={`${testPrefix}-dr-ledger`} placeholder={`Auto — ${autoDr}`} value={drId || ""} onChange={v => onChange({ dr_ledger_id: v })}
            options={opts} onCreateNew={(t) => openNew("dr", t)} />
        </div>
        <div>
          <div className="text-xs text-ink-muted mb-1">Ledger to <b>CREDIT</b> ({crHint}) · default <span className="font-mono">{autoCr}</span></div>
          <SearchableSelect testId={`${testPrefix}-cr-ledger`} placeholder={`Auto — ${autoCr}`} value={crId || ""} onChange={v => onChange({ cr_ledger_id: v })}
            options={opts} onCreateNew={(t) => openNew("cr", t)} />
        </div>
      </div>
      <div className="text-[11px] text-ink-muted">{sales ? "Output" : "Input"} CGST / SGST / IGST post automatically. Type a name that doesn't exist and press <b>Alt+C</b> to create it on the spot.</div>
      {modal && <LedgerFormModal initial={modal.initial} groups={groups} onClose={() => setModal(null)}
        onSaved={async (l) => { await reload(); onChange(modal.side === "dr" ? { dr_ledger_id: l.id } : { cr_ledger_id: l.id }); setModal(null); }} />}
    </div>
  );
}

export function fmtOpening(l) { return `${fmtINR(l.opening_balance)} ${(l.opening_type || "dr").toUpperCase()}`; }
